/**
 * MacroCalculatorDialog — wizard that computes personalised daily macro
 * targets using Mifflin-St Jeor BMR + TDEE adjustment for activity level
 * and goal (cut / maintain / bulk). User can save the result as their
 * food-log daily targets in one tap.
 *
 * Formula notes:
 *   BMR (Mifflin-St Jeor):
 *     men:   10·kg + 6.25·cm − 5·age + 5
 *     women: 10·kg + 6.25·cm − 5·age − 161
 *   TDEE = BMR × activity multiplier
 *   Macros default split (post-goal-adjustment):
 *     - protein 1.0 g/lb body weight (1.1 for cut)
 *     - fat     0.35 g/lb body weight
 *     - carbs   remaining calories / 4
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Calculator, Save, TrendingDown, Minus, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_LEVELS = [
  { key: 'sedentary', label: 'Sedentary', sub: 'desk job, little/no exercise', mult: 1.2 },
  { key: 'light',     label: 'Light',     sub: '1-3 workouts / week',          mult: 1.375 },
  { key: 'moderate',  label: 'Moderate',  sub: '3-5 workouts / week',          mult: 1.55 },
  { key: 'very',      label: 'Very Active', sub: '6-7 workouts / week',        mult: 1.725 },
  { key: 'athlete',   label: 'Athlete',   sub: '2× per day or physical job',   mult: 1.9 },
];

const GOALS = [
  { key: 'cut',      label: 'Fat Loss',     sub: '−20% calories', Icon: TrendingDown, adjust: -0.20, proteinPerLb: 1.1 },
  { key: 'maintain', label: 'Maintain',     sub: 'eat at TDEE',   Icon: Minus,        adjust: 0,     proteinPerLb: 1.0 },
  { key: 'bulk',     label: 'Muscle Gain',  sub: '+10% calories', Icon: TrendingUp,   adjust: 0.10,  proteinPerLb: 1.0 },
];

const kgFromLbs = (lbs) => lbs * 0.453592;
const cmFromInches = (inches) => inches * 2.54;

export const MacroCalculatorDialog = ({ open, onClose, initialWeightLb, initialHeightIn, initialAge, initialSex, onSaved }) => {
  const { token } = useAuth();
  const [mode, setMode] = useState('calc'); // 'calc' | 'manual'
  const [sex, setSex] = useState(initialSex || 'male');
  const [age, setAge] = useState(initialAge ? String(initialAge) : '');
  const [heightFt, setHeightFt] = useState(initialHeightIn ? String(Math.floor(initialHeightIn / 12)) : '');
  const [heightIn, setHeightIn] = useState(initialHeightIn ? String(initialHeightIn % 12) : '');
  const [weightLb, setWeightLb] = useState(initialWeightLb ? String(Math.round(initialWeightLb)) : '');
  const [activity, setActivity] = useState('moderate');
  const [goal, setGoal] = useState('maintain');
  const [saving, setSaving] = useState(false);

  // Manual entry state — lets users type exact numbers from a coach or lab test.
  const [manualCalories, setManualCalories] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  const [manualFat, setManualFat] = useState('');

  const result = useMemo(() => {
    const a = parseFloat(age);
    const w = parseFloat(weightLb);
    const hIn = (parseFloat(heightFt) || 0) * 12 + (parseFloat(heightIn) || 0);
    if (!a || !w || !hIn) return null;
    const kg = kgFromLbs(w);
    const cm = cmFromInches(hIn);
    const bmr = sex === 'male'
      ? 10 * kg + 6.25 * cm - 5 * a + 5
      : 10 * kg + 6.25 * cm - 5 * a - 161;
    const actObj = ACTIVITY_LEVELS.find((x) => x.key === activity);
    const tdee = bmr * actObj.mult;
    const goalObj = GOALS.find((x) => x.key === goal);
    const calories = Math.round(tdee * (1 + goalObj.adjust));
    const protein_g = Math.round(w * goalObj.proteinPerLb);
    const fat_g = Math.round(w * 0.35);
    const remainingCal = calories - (protein_g * 4) - (fat_g * 9);
    const carbs_g = Math.max(0, Math.round(remainingCal / 4));
    return { bmr: Math.round(bmr), tdee: Math.round(tdee), calories, protein_g, fat_g, carbs_g };
  }, [age, weightLb, heightFt, heightIn, sex, activity, goal]);

  const save = async () => {
    let payload;
    if (mode === 'manual') {
      const cal = parseFloat(manualCalories);
      const p = parseFloat(manualProtein);
      const c = parseFloat(manualCarbs);
      const f = parseFloat(manualFat);
      if (!cal || cal < 1 || cal > 10000) { toast.error('Enter a valid calorie total (1-10000)'); return; }
      if (isNaN(p) || isNaN(c) || isNaN(f)) { toast.error('Fill in all four macros'); return; }
      payload = { calories: Math.round(cal), protein_g: Math.round(p), carbs_g: Math.round(c), fat_g: Math.round(f) };
    } else {
      if (!result) return;
      payload = { calories: result.calories, protein_g: result.protein_g, carbs_g: result.carbs_g, fat_g: result.fat_g };
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/food-log/targets`,
        payload,
        { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      toast.success('Daily targets saved');
      onSaved?.(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/85 p-4 overflow-y-auto" onClick={onClose} data-testid="macro-calc-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-primary/40 rounded-2xl max-w-xl w-full my-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" /> Macro Calculator
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {/* Mode toggle: Calculator vs Manual Entry */}
        <div className="flex bg-black/60 border border-border rounded-full p-0.5 mb-4" data-testid="macro-calc-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('calc')}
            className={`flex-1 py-1.5 text-[11px] font-heading uppercase tracking-wider rounded-full transition-colors ${mode === 'calc' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
            data-testid="macro-calc-mode-calc"
          >
            Calculator
          </button>
          <button
            type="button"
            onClick={() => setMode('manual')}
            className={`flex-1 py-1.5 text-[11px] font-heading uppercase tracking-wider rounded-full transition-colors ${mode === 'manual' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
            data-testid="macro-calc-mode-manual"
          >
            Manual Entry
          </button>
        </div>

        {mode === 'manual' ? (
          <div className="space-y-3" data-testid="macro-calc-manual-section">
            <p className="text-xs text-muted-foreground">
              Enter exact daily targets — use this if your coach, lab, or dietitian gave you specific numbers.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Calories</label>
                <Input type="number" value={manualCalories} onChange={(e) => setManualCalories(e.target.value)} placeholder="3000" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-manual-calories" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Protein (g)</label>
                <Input type="number" value={manualProtein} onChange={(e) => setManualProtein(e.target.value)} placeholder="200" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-manual-protein" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Carbs (g)</label>
                <Input type="number" value={manualCarbs} onChange={(e) => setManualCarbs(e.target.value)} placeholder="380" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-manual-carbs" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fat (g)</label>
                <Input type="number" value={manualFat} onChange={(e) => setManualFat(e.target.value)} placeholder="75" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-manual-fat" />
              </div>
            </div>
            {(manualProtein && manualCarbs && manualFat) && (
              <p className="text-[10px] text-muted-foreground text-center">
                Macro math check: {Math.round((parseFloat(manualProtein) || 0) * 4 + (parseFloat(manualCarbs) || 0) * 4 + (parseFloat(manualFat) || 0) * 9)} kcal from macros
              </p>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-primary text-black hover:bg-primary/90"
              data-testid="macro-calc-manual-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as my daily targets
            </Button>
          </div>
        ) : (
        <>
        {/* Personal info */}
        <div className="space-y-3">
          <div className="flex gap-2">
            {['male', 'female'].map((s) => (
              <button
                key={s}
                onClick={() => setSex(s)}
                className={`flex-1 py-2 text-xs uppercase tracking-wider rounded-md border ${
                  sex === s ? 'bg-primary text-black border-primary' : 'bg-card text-muted-foreground border-border'
                }`}
                data-testid={`macro-calc-sex-${s}`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Age</label>
              <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="30" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-age" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Weight (lb)</label>
              <Input type="number" value={weightLb} onChange={(e) => setWeightLb(e.target.value)} placeholder="170" className="bg-card border-border text-white mt-0.5" data-testid="macro-calc-weight" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Height</label>
            <div className="flex gap-2 mt-0.5">
              <Input type="number" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} placeholder="5" className="bg-card border-border text-white" data-testid="macro-calc-height-ft" />
              <span className="text-muted-foreground self-center text-xs">ft</span>
              <Input type="number" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} placeholder="10" className="bg-card border-border text-white" data-testid="macro-calc-height-in" />
              <span className="text-muted-foreground self-center text-xs">in</span>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Activity Level</label>
            <div className="flex flex-col gap-1.5 mt-1">
              {ACTIVITY_LEVELS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setActivity(a.key)}
                  className={`text-left px-3 py-2 rounded-md border text-sm flex items-center justify-between gap-3 ${
                    activity === a.key ? 'bg-primary/20 text-primary border-primary' : 'bg-card text-muted-foreground border-border'
                  }`}
                  data-testid={`macro-calc-activity-${a.key}`}
                >
                  <div>
                    <p className="text-white text-xs font-medium">{a.label}</p>
                    <p className="text-[10px] text-muted-foreground">{a.sub}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground">×{a.mult}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Goal</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {GOALS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGoal(g.key)}
                  className={`p-2 rounded-md border text-center ${
                    goal === g.key ? 'bg-primary/20 text-primary border-primary' : 'bg-card text-muted-foreground border-border'
                  }`}
                  data-testid={`macro-calc-goal-${g.key}`}
                >
                  <g.Icon className="h-4 w-4 mx-auto mb-1" />
                  <p className="text-[11px] text-white font-medium">{g.label}</p>
                  <p className="text-[9px] text-muted-foreground">{g.sub}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result */}
        {result ? (
          <div className="mt-5 bg-black/40 border border-primary/30 rounded-xl p-4" data-testid="macro-calc-result">
            <div className="flex items-baseline justify-between mb-3">
              <p className="font-heading text-sm text-white uppercase tracking-wider">Your Daily Targets</p>
              <p className="text-[10px] text-muted-foreground">BMR {result.bmr} · TDEE {result.tdee}</p>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <Macro label="Calories" value={result.calories} color="text-yellow-400" />
              <Macro label="Protein" value={`${result.protein_g}g`} color="text-green-400" />
              <Macro label="Carbs" value={`${result.carbs_g}g`} color="text-blue-400" />
              <Macro label="Fat" value={`${result.fat_g}g`} color="text-orange-400" />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="mt-4 w-full bg-primary text-black hover:bg-primary/90"
              data-testid="macro-calc-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as my daily targets
            </Button>
          </div>
        ) : (
          <p className="mt-4 text-xs text-muted-foreground text-center">Fill in age, height & weight to see your targets.</p>
        )}
        </>
        )}
      </div>
    </div>
  );
};

const Macro = ({ label, value, color }) => (
  <div>
    <p className={`font-heading text-xl ${color}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);
